import type { ComponentType } from 'react'
import type { PageFrontmatter } from './types/page'
import { navSections } from './nav-data'

interface MdxModule {
  default: ComponentType
  frontmatter: PageFrontmatter
}

// Every page is one `index.mdx` under src/content. The folder path *is* the
// URL — there is no registration step. Drop in a file, the page exists.
//
// `eager` means pages ship in the initial bundle: crawlers see real HTML and
// there is no loading flash. When the doc set crosses ~100 pages, switch this
// to a lazy `import()` and render leaves through Suspense.
const modules = import.meta.glob<MdxModule>('./content/**/index.mdx', {
  eager: true,
})

export interface NavLeaf {
  slug: string
  title: string
  description: string
  category: string
  section: string
  order: number
  /** Slug of the hub this page nests under, or undefined for a top-level page. */
  parent?: string
  Component: ComponentType
  frontmatter: PageFrontmatter
}

/** A top-level sidebar entry (a hub or a standalone page) plus its children. */
export interface NavTreeNode extends NavLeaf {
  children: NavLeaf[]
}

export interface NavSection {
  title: string
  items: NavTreeNode[]
}

/** `./content/guides/candidates/index.mdx` -> `/guides/candidates` */
export function slugFromContentPath(path: string): string {
  const match = path.match(/^\.\/content\/(.+)\/index\.mdx$/)
  if (!match) {
    throw new Error(`nav: cannot derive a slug from content path "${path}".`)
  }
  return `/${match[1]}`
}

function toLeaf(path: string, mod: MdxModule): NavLeaf {
  const fm = mod.frontmatter
  if (
    !fm ||
    typeof fm.nav?.section !== 'string' ||
    typeof fm.nav?.order !== 'number'
  ) {
    throw new Error(
      `nav: ${path} is missing required frontmatter ` +
        `"nav: { section: string, order: number }".`,
    )
  }
  return {
    slug: slugFromContentPath(path),
    title: fm.title,
    description: fm.description,
    category: fm.category,
    section: fm.nav.section,
    order: fm.nav.order,
    parent: fm.nav.parent,
    Component: mod.default,
    frontmatter: fm,
  }
}

const leaves: NavLeaf[] = Object.entries(modules).map(([path, mod]) =>
  toLeaf(path, mod),
)

const sectionOrder = new Map(navSections.map((s) => [s.id, s.order]))
const byOrder = (a: NavLeaf, b: NavLeaf): number =>
  a.order - b.order || a.slug.localeCompare(b.slug)

// Sidebar nav: sections in spec order; within a section, hub pages (and
// standalone pages) at the top level with their sub-pages nested beneath.
// A page whose `parent` doesn't resolve inside the section is shown at the
// top level rather than vanishing — the content validator flags the typo.
export const nav: NavSection[] = [...navSections]
  .sort((a, b) => a.order - b.order)
  .map((section): NavSection => {
    const inSection = leaves.filter((leaf) => leaf.section === section.id)
    const slugs = new Set(inSection.map((leaf) => leaf.slug))
    const isTopLevel = (leaf: NavLeaf): boolean =>
      !leaf.parent || !slugs.has(leaf.parent)
    return {
      title: section.title,
      items: inSection
        .filter(isTopLevel)
        .sort(byOrder)
        .map((node): NavTreeNode => ({
          ...node,
          children: inSection
            .filter((leaf) => !isTopLevel(leaf) && leaf.parent === node.slug)
            .sort(byOrder),
        })),
    }
  })
  .filter((section) => section.items.length > 0)

// Every page, regardless of section or nesting — this drives routing, so a
// page with a typo'd section or parent still resolves to a working URL.
export function flattenNav(): NavLeaf[] {
  return [...leaves].sort((a, b) => {
    const sa = sectionOrder.get(a.section) ?? Number.MAX_SAFE_INTEGER
    const sb = sectionOrder.get(b.section) ?? Number.MAX_SAFE_INTEGER
    return sa - sb || byOrder(a, b)
  })
}

export function findLeaf(slug: string): NavLeaf | undefined {
  return leaves.find((leaf) => leaf.slug === slug)
}
